import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Building, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  Send,
  FileText,
  CreditCard,
  Package,
  Star,
  TrendingUp
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const SellerProfileModal = ({ isOpen, onClose, sellerId, onSellerUpdate }) => {
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [emailModal, setEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: '',
    pendingFields: []
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'business', label: 'Business Info', icon: Building },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'performance', label: 'Performance', icon: TrendingUp }
  ];

  useEffect(() => {
    if (isOpen && sellerId) {
      fetchSellerDetails();
    }
  }, [isOpen, sellerId]);

  const fetchSellerDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch real seller data from API
      const response = await api.get(`/admin/sellers/${sellerId}`);
      if (response.data.success) {
        const sellerData = response.data.data;
        
        // Add performance data (mock for now - can be fetched from separate endpoint)
        const sellerWithPerformance = {
          ...sellerData,
          performance: {
            totalProducts: 0,
            totalOrders: 0,
            totalRevenue: 0,
            rating: 0,
            completionRate: 0
          }
        };
        
        setSeller(sellerWithPerformance);
        calculateCompletionAndMissingFields(sellerWithPerformance);
      } else {
        toast.error('Failed to load seller details');
      }
    } catch (error) {
      console.error('Error fetching seller details:', error);
      toast.error('Failed to load seller details');
    } finally {
      setLoading(false);
    }
  };

  const calculateCompletionAndMissingFields = (sellerData) => {
    const requiredFields = [
      { field: 'fullName', label: 'Full Name', value: sellerData.fullName },
      { field: 'email', label: 'Email', value: sellerData.email },
      { field: 'phone', label: 'Phone', value: sellerData.phone },
      { field: 'businessName', label: 'Business Name', value: sellerData.businessName },
      { field: 'businessType', label: 'Business Type', value: sellerData.businessType },
      { field: 'gstNumber', label: 'GST Number', value: sellerData.gstNumber },
      { field: 'panNumber', label: 'PAN Number', value: sellerData.panNumber },
      { field: 'registeredAddress', label: 'Registered Address', value: sellerData.registeredAddress?.street },
      { field: 'bankDetails', label: 'Bank Details', value: sellerData.bankDetails?.accountNumber },
      { field: 'selectedCategories', label: 'Business Categories', value: sellerData.selectedCategories?.length > 0 }
    ];

    const missingFields = requiredFields.filter(field => !field.value).map(field => field.label);
    
    setEmailData(prev => ({
      ...prev,
      pendingFields: missingFields,
      subject: missingFields.length > 0 ? 'Complete Your Seller Profile' : 'Profile Review Update',
      message: missingFields.length > 0 
        ? `Dear ${sellerData.fullName || 'Seller'},\n\nWe noticed that your seller profile is incomplete. Please complete the following fields to activate your account:\n\n${missingFields.map(field => `• ${field}`).join('\n')}\n\nOnce completed, we'll review and approve your account within 24 hours.\n\nBest regards,\nBuildify Team`
        : `Dear ${sellerData.fullName || 'Seller'},\n\nYour seller profile has been reviewed. We'll get back to you shortly with the approval status.\n\nBest regards,\nBuildify Team`
    }));
  };

  const getCompletionPercentage = () => {
    if (!seller) return 0;
    
    const fields = [
      seller.fullName, seller.email, seller.phone, seller.businessName,
      seller.businessType, seller.gstNumber, seller.panNumber,
      seller.registeredAddress?.street, seller.bankDetails?.accountNumber,
      seller.selectedCategories?.length > 0
    ];
    
    const completedFields = fields.filter(field => field).length;
    return Math.round((completedFields / fields.length) * 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleSendEmail = async () => {
    try {
      if (!emailData.subject || !emailData.message) {
        toast.error('Please fill in subject and message');
        return;
      }

      // Here you would call the API to send email
      // await api.post(`/admin/sellers/${sellerId}/send-email`, emailData);
      
      toast.success('Email sent successfully!');
      setEmailModal(false);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      let response;
      if (newStatus === 'approved') {
        response = await api.patch(`/admin/sellers/${sellerId}/approve`);
      } else if (newStatus === 'rejected') {
        response = await api.patch(`/admin/sellers/${sellerId}/reject`, {
          rejectionReason: 'Rejected by admin'
        });
      }
      
      if (response && response.data.success) {
        setSeller(prev => ({ 
          ...prev, 
          status: newStatus, 
          isApproved: newStatus === 'approved',
          approvalDate: newStatus === 'approved' ? new Date().toISOString() : null
        }));
        toast.success(`Seller ${newStatus} successfully!`);
        onSellerUpdate && onSellerUpdate();
      }
    } catch (error) {
      console.error('Error updating seller status:', error);
      toast.error(error.response?.data?.message || 'Failed to update seller status');
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Profile Completion */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Profile Completion</h4>
          <span className="text-sm font-medium text-gray-600">{getCompletionPercentage()}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${getCompletionPercentage() >= 80 ? 'bg-green-500' : getCompletionPercentage() >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
            style={{ width: `${getCompletionPercentage()}%` }}
          />
        </div>
        {emailData.pendingFields.length > 0 && (
          <div className="mt-3">
            <p className="text-sm text-red-600 mb-2">Missing Fields:</p>
            <ul className="text-xs text-red-500 space-y-1">
              {emailData.pendingFields.map((field, index) => (
                <li key={index}>• {field}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Full Name</p>
              <p className="font-medium">{seller?.fullName || 'Not provided'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Mail className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{seller?.email || 'Not provided'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Phone className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p className="font-medium">{seller?.phone || 'Not provided'}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <Building className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Business Name</p>
              <p className="font-medium">{seller?.businessName || 'Not provided'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Joined</p>
              <p className="font-medium">
                {seller?.createdAt ? new Date(seller.createdAt).toLocaleDateString() : 'Not available'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <MapPin className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium">
                {seller?.registeredAddress ? 
                  `${seller.registeredAddress.city}, ${seller.registeredAddress.state}` : 
                  'Not provided'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(seller?.status)}`}>
            {seller?.status || 'Unknown'}
          </span>
          {seller?.isRegistrationComplete ? (
            <span className="flex items-center text-green-600 text-sm">
              <CheckCircle className="w-4 h-4 mr-1" />
              Registration Complete
            </span>
          ) : (
            <span className="flex items-center text-yellow-600 text-sm">
              <Clock className="w-4 h-4 mr-1" />
              Registration Incomplete
            </span>
          )}
        </div>
        
        <div className="flex space-x-2">
          {seller?.status !== 'approved' && (
            <button
              onClick={() => handleStatusUpdate('approved')}
              className="btn btn-success btn-sm"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Approve
            </button>
          )}
          {seller?.status !== 'rejected' && (
            <button
              onClick={() => handleStatusUpdate('rejected')}
              className="btn btn-danger btn-sm"
            >
              <X className="w-4 h-4 mr-1" />
              Reject
            </button>
          )}
          <button
            onClick={() => setEmailModal(true)}
            className="btn btn-primary btn-sm"
          >
            <Send className="w-4 h-4 mr-1" />
            Send Email
          </button>
        </div>
      </div>
    </div>
  );

  const renderBusinessTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Business Type</label>
          <p className="mt-1 text-sm text-gray-900">{seller?.businessType || 'Not provided'}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">GST Number</label>
          <p className="mt-1 text-sm text-gray-900">{seller?.gstNumber || 'Not provided'}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">PAN Number</label>
          <p className="mt-1 text-sm text-gray-900">{seller?.panNumber || 'Not provided'}</p>
        </div>
      </div>
      
      {/* Address */}
      {seller?.registeredAddress && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Registered Address</label>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-900">
              {seller.registeredAddress.street}<br/>
              {seller.registeredAddress.city}, {seller.registeredAddress.state} - {seller.registeredAddress.pincode}
            </p>
          </div>
        </div>
      )}

      {/* Bank Details */}
      {seller?.bankDetails && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bank Details</label>
          <div className="bg-gray-50 p-3 rounded-lg space-y-2">
            <p className="text-sm"><span className="font-medium">Account Holder:</span> {seller.bankDetails.accountHolderName}</p>
            <p className="text-sm"><span className="font-medium">Account Number:</span> ****{seller.bankDetails.accountNumber?.slice(-4)}</p>
            <p className="text-sm"><span className="font-medium">IFSC Code:</span> {seller.bankDetails.ifscCode}</p>
            <p className="text-sm"><span className="font-medium">Bank:</span> {seller.bankDetails.bankName}</p>
          </div>
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-primary-600 text-white p-6 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Seller Profile</h2>
              <p className="text-primary-100 mt-1">{seller?.businessName || 'Loading...'}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-primary-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                          activeTab === tab.id
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'business' && renderBusinessTab()}
                {activeTab === 'documents' && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>Document verification will be implemented here</p>
                  </div>
                )}
                {activeTab === 'performance' && (
                  <div className="text-center py-8 text-gray-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>Performance metrics will be implemented here</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Send Email to Seller</h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="input-field"
                  placeholder="Enter email subject"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                  className="input-field"
                  rows="8"
                  placeholder="Enter your message"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setEmailModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                className="btn btn-primary flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SellerProfileModal;
